/**
 * Despacha os painéis de correlação declarados no descriptor. Cada painel recebe o
 * dono (ownerTipo + id do item) e uma função para recarregar a entidade quando algo
 * muda. `custom` injeta JSX arbitrário reaproveitando o mesmo lugar na página.
 */
import { PainelContatos } from '@/components/crud/paineis/PainelContatos';
import { PainelDocumentos } from '@/components/crud/paineis/PainelDocumentos';
import { PainelDossie } from '@/components/crud/paineis/PainelDossie';
import { PainelEmpregos } from '@/components/crud/paineis/PainelEmpregos';
import { PainelGrafo } from '@/components/crud/paineis/PainelGrafo';
import { PainelEnderecos } from '@/components/crud/paineis/PainelEnderecos';
import { PainelPapeisCondominio } from '@/components/crud/paineis/PainelPapeisCondominio';
import { PainelRelacoes } from '@/components/crud/paineis/PainelRelacoes';
import { PainelTags } from '@/components/crud/paineis/PainelTags';
import { PainelVinculos } from '@/components/crud/paineis/PainelVinculos';
import type { DescritorRecurso, PainelCorrelacao, RecursoBase } from '@/lib/recursos/tipos';

export function PaineisEdicao({
  descritor,
  item,
  recarregar,
  paineis,
}: {
  descritor: DescritorRecurso;
  item: RecursoBase;
  recarregar: () => Promise<void>;
  /** Subconjunto a renderizar (uma aba passa os seus); ausente → todos os do descriptor. */
  paineis?: PainelCorrelacao<RecursoBase>[];
}) {
  const lista = paineis ?? descritor.paineis;
  return (
    <div className="flex flex-col gap-6">
      {lista?.map((painel, i) => {
        switch (painel.tipo) {
          case 'tags':
            return descritor.ownerTipo ? (
              <PainelTags key={i} ownerTipo={descritor.ownerTipo} ownerId={item.id} />
            ) : null;
          case 'contatos':
            return descritor.ownerTipo ? (
              <PainelContatos key={i} ownerTipo={descritor.ownerTipo} ownerId={item.id} />
            ) : null;
          case 'enderecos':
            return descritor.ownerTipo ? (
              <PainelEnderecos key={i} ownerTipo={descritor.ownerTipo} ownerId={item.id} />
            ) : null;
          case 'documentos':
            return descritor.ownerTipo ? (
              <PainelDocumentos
                key={i}
                ownerTipo={descritor.ownerTipo}
                ownerId={item.id}
                recarregarEntidade={recarregar}
              />
            ) : null;
          case 'relacoes':
            return <PainelRelacoes key={i} pessoaId={item.id} />;
          case 'empregos':
            return <PainelEmpregos key={i} pessoaId={item.id} />;
          case 'papeis':
            return <PainelPapeisCondominio key={i} condominioId={item.id} />;
          case 'dossie':
            return descritor.ownerTipo ? (
              <PainelDossie key={i} ownerTipo={descritor.ownerTipo} ownerId={item.id} />
            ) : null;
          case 'grafo':
            return descritor.ownerTipo ? (
              <PainelGrafo key={i} ownerTipo={descritor.ownerTipo} ownerId={item.id} />
            ) : null;
          case 'vinculos':
            return (
              <PainelVinculos
                key={i}
                titulo={painel.titulo}
                endpointGet={painel.endpointGet?.replace('{id}', item.id)}
                endpointPost={painel.endpointPost}
                ownerId={item.id}
              />
            );
          case 'custom':
            return <div key={painel.id}>{painel.render({ item, recarregar })}</div>;
          default:
            return null;
        }
      })}
    </div>
  );
}
